import { FC } from 'react';
import { Box, Chip, MenuItem, Paper, TextField, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react';

import { hasValue, isBlank, isProfileSavable, type PendingPlaceholder } from '../utils/placeholders';

/**
 * The control a profile field's declared type deserves.
 *
 * Everything unrecognised — and every blank with no profile field at all — is
 * free text. That is the honest default rather than a fallback pretending to
 * know: guessing `number` for a field the template calls something else would
 * reject values the template would have accepted.
 */
function controlFor(type: string | undefined, options: string[] | null | undefined) {
  if (options?.length) return 'select' as const;
  switch (type) {
    case 'date':
      return 'date' as const;
    case 'number':
    case 'integer':
      return 'number' as const;
    case 'email':
      return 'email' as const;
    case 'url':
      return 'url' as const;
    default:
      return 'text' as const;
  }
}

/**
 * One blank, with what filling it does shown beside the box that fills it.
 *
 * The preview leads because it is the answer to the question the user actually
 * has — "what am I changing?" A label like `[REVIEWER_POSITION]` names a slot;
 * the sentence it sits in is what says whether the value you are about to type
 * is the right kind of thing.
 */
export const PlaceholderFillCard: FC<{
  placeholder: PendingPlaceholder;
  value: string;
  onChange: (value: string) => void;
  /** Tells the page which blank to bring into view in the document beside it. */
  onFocus: () => void;
  /** Emphasised while this is the blank the document is scrolled to. */
  active?: boolean;
}> = observer(({ placeholder, value, onChange, onFocus, active }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const known = placeholder.field ? hasValue(placeholder.field.currentValue) : false;
  // The same test the footer count and the submit dialog apply, rather than a
  // second copy of it here — the two drifting is how a card comes to promise
  // something the list beside it contradicts.
  const willStayBlank = isBlank(placeholder, value);
  const inputKind = controlFor(placeholder.field?.type, placeholder.field?.options);
  // Matching a profile field is not the same as being able to write to it: a
  // blank matched to a table or file field has nowhere to put one typed line,
  // and promising otherwise here would be a promise the save cannot keep.
  const savesToProfile = isProfileSavable(placeholder.field);

  return (
    <Paper
      elevation={0}
      onClick={onFocus}
      sx={{
        p: 2,
        borderRadius: 3,
        cursor: 'text',
        border: `1px solid ${active ? theme.palette.ocean[700] : theme.palette.grey[300]}`,
        backgroundColor: active ? theme.palette.ocean[100] : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography variant="textSM_semiBold">{placeholder.label}</Typography>
        {/* What answering does. A value the profile has a field for is kept and
            reused by every future document; one it does not — a procedure
            number, an issue date — belongs to this document alone. Both are
            worth typing; only one survives the page, and this is where that is
            said rather than discovered. */}
        <Chip
          size="small"
          icon={savesToProfile ? <PencilSimpleIcon size={11} /> : undefined}
          label={t(
            savesToProfile
              ? 'app.remediation.placeholders.savesToProfile'
              : 'app.remediation.placeholders.documentOnly',
          )}
          sx={{
            height: 20,
            fontSize: 11,
            fontWeight: 600,
            ...(savesToProfile
              ? {
                  backgroundColor: theme.palette.ocean[100],
                  color: theme.palette.ocean[700],
                  '& .MuiChip-icon': { color: 'inherit' },
                }
              : {}),
          }}
        />
        {known && (
          <Chip
            size="small"
            icon={<CheckIcon size={11} />}
            label={t('app.remediation.placeholders.alreadyKnown')}
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 600,
              backgroundColor: theme.palette.green[200],
              color: theme.palette.green[800],
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        )}
      </Box>

      {/* Just the control. The document beside it is the preview now — a
          cropped sentence per field answered "what does this word sit next to"
          and nothing else, and repeating it per card meant reading the same
          paragraph four times. */}
      <TextField
        fullWidth
        select={inputKind === 'select'}
        type={inputKind === 'select' ? undefined : inputKind}
        size="small"
        value={value}
        onFocus={onFocus}
        onChange={(e) => onChange(e.target.value)}
        /* What skipping costs, said where skipping happens, and carried by the
           input's own helper slot so it is announced with the field rather than
           read as loose text beside it. A blank may be left blank — the user may
           not know the answer today — but the token then appears verbatim in the
           finished document, and a downloaded PDF is a poor place to learn that. */
        helperText={
          willStayBlank
            ? t('app.remediation.placeholders.willStayBlank', { token: placeholder.token })
            : undefined
        }
        slotProps={{
          ...(inputKind === 'date' ? { inputLabel: { shrink: true } } : {}),
          formHelperText: { sx: { mx: 0, color: 'text.tertiary' } },
        }}
        placeholder={
          known
            ? String(placeholder.field?.currentValue)
            : t('app.remediation.placeholders.inputHint')
        }
      >
        {inputKind === 'select' &&
          (placeholder.field?.options ?? []).map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
      </TextField>
    </Paper>
  );
});
